/**
 * stratevacScheduling.js
 * PSP/CRE Tool — Strategic Evacuation Scheduling Constants
 *
 * @version 1.1.0
 * @date 2026-03-07
 * @author Dr. Tatar Radu MD
 *
 * PURPOSE:
 *   Constants for ENH-003 Scheduled STRATEVAC (Aircraft).
 *   Defines aircraft types, flight schedules, PHA thresholds,
 *   nationality routing rules, airfield classifications,
 *   and default schedule templates.
 *
 * DOCTRINE:
 *   AJMedP-1 §6 — Strategic Medical Evacuation
 *   AJP-4.10C — Allied Joint Medical Support
 *   NATO MC 326/4 — STRATEVAC policy
 *   AMedP-7.1 — Medical Logistics
 *
 * ARCHITECTURE:
 *   Constants only — zero logic.
 *   All scheduling logic lives in casualtyCalculator.js.
 *   Tab3 reads these for UI dropdowns and defaults.
 *   Tab5 reads these for display labels.
 *
 * AIRFIELD SCHEDULE STATE SHAPE (written by Tab3, read by calculator):
 *   strategicEvac.airfields[n].schedule = {
 *     aircraft_type:        string   — key from STRATEVAC_AIRCRAFT
 *     nationality:          string   — key from nationalStandards.js
 *     frequency_mode:       string   — key from SCHEDULE_FREQUENCY_MODES
 *     fixed_days:           number[] — departure days (1-based) if mode = FIXED_DAYS
 *     interval_hours:       number   — interval if mode = INTERVAL_HOURS
 *     first_departure_day:  number   — day 1-based when first flight occurs
 *     departure_hour:       number   — 0–23 UTC
 *     nationality_routing:  boolean  — restrict to own-nation patients
 *     accepting_nations:    string[] — nations accepted if routing = false
 *     flights_per_day:      number   — aircraft sorties on each departure day (default 1)
 *                                      e.g. 4 = four C-130 sorties on each scheduled day
 *   }
 *
 * CHANGELOG:
 *   v1.1.0 — 2026-03-07 — ENH-STRAT-001: flights_per_day field.
 *     Added to schedule state shape, DEFAULT_SCHEDULE_TEMPLATES (all 7 entries, default=1),
 *     and FLIGHTS_PER_DAY_OPTIONS UI constant. Calculator reads schedule.flights_per_day ?? 1
 *     and multiplies per-day litter capacity accordingly.
 *   v1.0.0 — 2026-03-06 — ENH-003 Phase 1. Initial constants file.
 */

// =============================================================================
// AIRCRAFT TYPES
// =============================================================================

/**
 * STRATEVAC_AIRCRAFT
 * Aircraft available for strategic medical evacuation.
 * Capacity values are NATO-standard patient loads (AJMedP-1 Annex B).
 *
 * litter_capacity:      maximum litter (T1/T2 non-ambulatory) patients
 * ambulatory_capacity:  maximum ambulatory (T3) patients
 * total_capacity:       combined maximum (litter + ambulatory, mixed config)
 * icu_capable:          aircraft has in-flight ICU capability
 * range_km:             combat radius (no refuel)
 * cruise_speed_kmh:     planning speed
 * turnaround_hours:     minimum ground time between sorties (offload + reload)
 * nations:              NATO nations operating this type
 * doctrinal_note:       reference for capacity figures
 */
export const STRATEVAC_AIRCRAFT = {
  C130J: {
    label: 'C-130J Hercules',
    litter_capacity: 74,
    ambulatory_capacity: 92,
    total_capacity: 74,          // planning figure: litter-configured
    icu_capable: false,
    range_km: 3334,
    cruise_speed_kmh: 643,
    turnaround_hours: 2,
    nations: ['ROMANIAN', 'US', 'BRITISH', 'ITALIAN', 'FRENCH', 'POLISH'],
    doctrinal_note: 'AJMedP-1 Annex B Table B-2. Litter config = 74 patients.',
  },
  C17: {
    label: 'C-17 Globemaster III',
    litter_capacity: 36,
    ambulatory_capacity: 102,
    total_capacity: 36,          // planning figure: litter-configured
    icu_capable: true,
    range_km: 4630,
    cruise_speed_kmh: 833,
    turnaround_hours: 3,
    nations: ['US', 'BRITISH', 'NATO'],
    doctrinal_note: 'AJMedP-1 Annex B Table B-2. Litter config = 36 patients. ICU-capable.',
  },
  A400M: {
    label: 'A400M Atlas',
    litter_capacity: 66,
    ambulatory_capacity: 116,
    total_capacity: 66,          // planning figure: litter-configured
    icu_capable: true,
    range_km: 3300,
    cruise_speed_kmh: 780,
    turnaround_hours: 2,
    nations: ['FRENCH', 'GERMAN', 'BRITISH', 'SPANISH', 'NATO'],
    doctrinal_note: 'AJMedP-1 Annex B Table B-3. Litter config = 66. ICU-capable with MEDEVAC module.',
  },
  IL76: {
    label: 'Il-76 Candid (medical)',
    litter_capacity: 114,
    ambulatory_capacity: 245,
    total_capacity: 114,
    icu_capable: false,
    range_km: 3600,
    cruise_speed_kmh: 780,
    turnaround_hours: 3,
    nations: ['ROMANIAN'],
    doctrinal_note: 'Romanian Air Force asset. Medical configuration per Romanian doctrine.',
  },
  GENERIC_TRANSPORT: {
    label: 'Generic Transport (planning)',
    litter_capacity: 40,
    ambulatory_capacity: 80,
    total_capacity: 40,
    icu_capable: false,
    range_km: 3000,
    cruise_speed_kmh: 700,
    turnaround_hours: 2,
    nations: ['NATO', 'MULTINATIONAL'],
    doctrinal_note: 'Planning figure when specific aircraft type not confirmed. Conservative estimate.',
  },
};

// =============================================================================
// SCHEDULE FREQUENCY MODES
// =============================================================================

/**
 * SCHEDULE_FREQUENCY_MODES
 * How flight departures are scheduled.
 *
 * FIXED_DAYS:      planner specifies exact operation days (e.g. days 1,3,5,7)
 * INTERVAL_HOURS:  planner specifies repeat interval (e.g. every 48h from day 1)
 * DAILY:           one flight per day (shorthand for INTERVAL_HOURS = 24)
 * ON_DEMAND:       no fixed schedule; departs when PHA reaches threshold
 *                  (exercise/Tab6 use only — not valid for Tabs 1–5 planning)
 */
export const SCHEDULE_FREQUENCY_MODES = {
  FIXED_DAYS: {
    label: 'Fixed Departure Days',
    description: 'Specify exact operation days for each departure.',
    planning_valid: true,
  },
  INTERVAL_HOURS: {
    label: 'Regular Interval',
    description: 'First departure day + repeat interval in hours.',
    planning_valid: true,
  },
  DAILY: {
    label: 'Daily',
    description: 'One flight per day starting from first departure day.',
    planning_valid: true,
  },
  ON_DEMAND: {
    label: 'On Demand (Exercise Only)',
    description: 'Departs when PHA threshold is reached. Tab 6 only.',
    planning_valid: false,
  },
};

// =============================================================================
// PHA THRESHOLDS
// =============================================================================

/**
 * PHA_THRESHOLDS
 * Pre-Hospital Area (PHA) occupancy thresholds that trigger alerts.
 * PHA is located at the STRATEVAC airfield — patients waiting for aircraft.
 *
 * Thresholds are expressed as fraction of pha_beds (0.0–1.0).
 * CRITICAL_WAIT_HOURS: maximum acceptable wait before MASCAL declaration.
 *
 * Source: AJMedP-1 §6.4, NATO MILMED COE PECC planning guidance.
 */
export const PHA_THRESHOLDS = {
  GREEN: {
    label: 'GREEN',
    min_fraction: 0.0,
    max_fraction: 0.50,
    description: 'Normal operations. Adequate STRATEVAC capacity.',
    color: 'success',
  },
  ELEVATED: {
    label: 'ELEVATED',
    min_fraction: 0.50,
    max_fraction: 0.75,
    description: 'Increased load. Monitor closely. Request additional sorties.',
    color: 'warning',
  },
  WARNING: {
    label: 'WARNING',
    min_fraction: 0.75,
    max_fraction: 1.00,
    description: 'Near capacity. Backflow to Role 3 imminent. Expedite sorties.',
    color: 'warning',
  },
  MASCAL: {
    label: 'MASCAL',
    min_fraction: 1.00,
    max_fraction: Infinity,
    description: 'PHA capacity exceeded. Backflow to Role 3 active. Corps MASCAL.',
    color: 'error',
  },
};

/**
 * Maximum patient wait time at PHA before MASCAL is declared.
 * Patients waiting longer than this require emergency sortie or Role 3 return.
 * Source: AJMedP-1 §6.4.3
 */
export const PHA_CRITICAL_WAIT_HOURS = 72;

/**
 * Maximum patient wait time before clinical deterioration risk increases.
 * Used for DOW modifier calculation in the calculator.
 * Source: AJMedP-1 §6.4.2
 */
export const PHA_WARNING_WAIT_HOURS = 48;

// =============================================================================
// NATIONALITY ROUTING
// =============================================================================

/**
 * NATIONALITY_ROUTING_MODES
 * Controls which patients a given aircraft accepts.
 *
 * OWN_NATION_ONLY: aircraft accepts patients of its operating nation only
 *   (e.g. Romanian C-130 takes Romanian patients; MOU required for others)
 * ALL_NATO:        aircraft accepts any NATO patient regardless of nationality
 * DESIGNATED:      aircraft accepts patients from specified nations (Tab3 config)
 *
 * Source: NATO SOFA, bilateral MOU frameworks. MC 0551.
 */
export const NATIONALITY_ROUTING_MODES = {
  OWN_NATION_ONLY: {
    label: 'Own Nation Only',
    description: 'Aircraft accepts patients of operating nation only.',
    requires_mou: false,
  },
  ALL_NATO: {
    label: 'All NATO Patients',
    description: 'Aircraft accepts any NATO patient. MOU in place.',
    requires_mou: true,
  },
  DESIGNATED: {
    label: 'Designated Nations',
    description: 'Aircraft accepts patients from specified nations per Tab 3 configuration.',
    requires_mou: true,
  },
};

/**
 * DEFAULT_NATIONALITY_ROUTING
 * Per-nation default routing mode.
 * Romanian doctrine: own-nation primary, ALL_NATO by MOU in multinational ops.
 */
export const DEFAULT_NATIONALITY_ROUTING = {
  ROMANIAN:      'OWN_NATION_ONLY',
  US:            'ALL_NATO',
  GERMAN:        'ALL_NATO',
  BRITISH:       'ALL_NATO',
  FRENCH:        'OWN_NATION_ONLY',
  POLISH:        'OWN_NATION_ONLY',
  NATO:          'ALL_NATO',
  MULTINATIONAL: 'ALL_NATO',
};

// =============================================================================
// AIRFIELD CLASSIFICATIONS
// =============================================================================

/**
 * AIRFIELD_CLASSES
 * NATO airfield classification for STRATEVAC planning.
 * Determines which aircraft types can operate.
 * QM-6I-001 fix: STANAG 3219 not found in any public NATO registry — reference removed.
 * Correct reference: STANAG 7131 (AEP-46) — Aircraft Classification Number (ACN)/
 *   Pavement Classification Number (PCN) — NATO airfield load-bearing classification standard.
 * STRATEVAC aeromedical mission doctrine: STANAG 3204 (AAMedP-1.1 Aeromedical Evacuation).
 */
export const AIRFIELD_CLASSES = {
  CLASS_A: {
    label: 'Class A — Strategic',
    description: 'Full-length paved runway. All aircraft types including C-17.',
    compatible_aircraft: ['C130J', 'C17', 'A400M', 'IL76', 'GENERIC_TRANSPORT'],
    min_runway_length_m: 2440,
  },
  CLASS_B: {
    label: 'Class B — Tactical',
    description: 'Medium paved runway. C-130J, A400M, Il-76. No C-17.',
    compatible_aircraft: ['C130J', 'A400M', 'IL76', 'GENERIC_TRANSPORT'],
    min_runway_length_m: 1830,
  },
  CLASS_C: {
    label: 'Class C — Forward',
    description: 'Short/unpaved strip. C-130J short-field only. Limited ops.',
    compatible_aircraft: ['C130J', 'GENERIC_TRANSPORT'],
    min_runway_length_m: 914,
  },
};

/**
 * AIRFIELD_TYPES
 * Ownership/operational status of the airfield.
 */
export const AIRFIELD_TYPES = {
  MILITARY:   { label: 'Military Airfield' },
  CIVILIAN:   { label: 'Civilian Airport (NATO use)' },
  JOINT:      { label: 'Joint Military/Civilian' },
  FORWARD:    { label: 'Forward Airfield (expeditionary)' },
};

// =============================================================================
// DEFAULT SCHEDULE TEMPLATES
// =============================================================================

/**
 * DEFAULT_SCHEDULE_TEMPLATES
 * Ready-made schedule templates for common national STRATEVAC patterns.
 * Used to pre-populate Tab3 schedule UI when airfield is first configured.
 * Planner can override any field after pre-population.
 *
 * Keys match national_standard values from nationalStandards.js.
 */
export const DEFAULT_SCHEDULE_TEMPLATES = {
  ROMANIAN: {
    aircraft_type: 'C130J',
    nationality: 'ROMANIAN',
    frequency_mode: 'FIXED_DAYS',
    fixed_days: [1, 3, 5, 7, 9],
    interval_hours: null,
    first_departure_day: 1,
    departure_hour: 6,           // 0600 local
    nationality_routing: 'OWN_NATION_ONLY',
    accepting_nations: ['ROMANIAN'],
    flights_per_day: 1,            // ENH-STRAT-001: default 1 sortie per departure day
    doctrinal_note: 'Romanian Air Force C-130J. Typical alternate-day STRATEVAC cycle.',
  },
  US: {
    aircraft_type: 'C17',
    nationality: 'US',
    frequency_mode: 'FIXED_DAYS',
    fixed_days: [2, 4, 6, 8, 10],
    interval_hours: null,
    first_departure_day: 2,
    departure_hour: 8,           // 0800 local
    nationality_routing: 'ALL_NATO',
    accepting_nations: ['US', 'NATO', 'MULTINATIONAL'],
    flights_per_day: 1,            // ENH-STRAT-001: default 1 sortie per departure day
    doctrinal_note: 'USAF C-17 AE mission. Interleaved with Romanian schedule. All-NATO routing by SOFA.',
  },
  GERMAN: {
    aircraft_type: 'A400M',
    nationality: 'GERMAN',
    frequency_mode: 'INTERVAL_HOURS',
    fixed_days: null,
    interval_hours: 48,
    first_departure_day: 1,
    departure_hour: 7,
    nationality_routing: 'ALL_NATO',
    accepting_nations: ['GERMAN', 'NATO'],
    flights_per_day: 1,            // ENH-STRAT-001: default 1 sortie per departure day
    doctrinal_note: 'Luftwaffe A400M. 48h interval. ALL_NATO routing per standing MOU.',
  },
  BRITISH: {
    aircraft_type: 'A400M',
    nationality: 'BRITISH',
    frequency_mode: 'INTERVAL_HOURS',
    fixed_days: null,
    interval_hours: 48,
    first_departure_day: 2,
    departure_hour: 6,
    nationality_routing: 'ALL_NATO',
    accepting_nations: ['BRITISH', 'NATO'],
    flights_per_day: 1,            // ENH-STRAT-001: default 1 sortie per departure day
    doctrinal_note: 'RAF A400M. 48h interval offset by 1 day from German schedule.',
  },
  FRENCH: {
    aircraft_type: 'A400M',
    nationality: 'FRENCH',
    frequency_mode: 'FIXED_DAYS',
    fixed_days: [1, 4, 7, 10],
    interval_hours: null,
    first_departure_day: 1,
    departure_hour: 7,
    nationality_routing: 'OWN_NATION_ONLY',
    accepting_nations: ['FRENCH'],
    flights_per_day: 1,            // ENH-STRAT-001: default 1 sortie per departure day
    doctrinal_note: 'Armée de l\'Air A400M. Own-nation routing unless MOU active.',
  },
  NATO: {
    aircraft_type: 'GENERIC_TRANSPORT',
    nationality: 'NATO',
    frequency_mode: 'INTERVAL_HOURS',
    fixed_days: null,
    interval_hours: 48,
    first_departure_day: 1,
    departure_hour: 6,
    nationality_routing: 'ALL_NATO',
    accepting_nations: ['NATO', 'MULTINATIONAL'],
    flights_per_day: 1,            // ENH-STRAT-001: default 1 sortie per departure day
    doctrinal_note: 'Generic NATO transport. Use when specific aircraft not yet confirmed.',
  },
  MULTINATIONAL: {
    aircraft_type: 'GENERIC_TRANSPORT',
    nationality: 'MULTINATIONAL',
    frequency_mode: 'INTERVAL_HOURS',
    fixed_days: null,
    interval_hours: 48,
    first_departure_day: 1,
    departure_hour: 6,
    nationality_routing: 'ALL_NATO',
    accepting_nations: ['NATO', 'MULTINATIONAL'],
    flights_per_day: 1,            // ENH-STRAT-001: default 1 sortie per departure day
    doctrinal_note: 'Multinational planning default. Replace with confirmed nation schedules.',
  },
};

// =============================================================================
// BACKFLOW RULES
// =============================================================================

/**
 * BACKFLOW_RULES
 * What happens when PHA exceeds capacity — patients back-flow to Role 3.
 *
 * PHA overflow does NOT return patients to Role 2B — Role 3 absorbs them.
 * Role 3 overflow then triggers CORPS MASCAL.
 *
 * Source: AJMedP-1 §6.4.4, NATO MILMED COE PECC presentations.
 */
export const BACKFLOW_RULES = {
  PHA_OVERFLOW_DESTINATION: 'ROLE_3',
  ROLE3_OVERFLOW_TRIGGERS_CORPS_MASCAL: true,

  /**
   * Maximum PHA overflow fraction before Role 3 backflow begins.
   * At 1.0 (100% PHA full) backflow starts immediately.
   */
  BACKFLOW_START_FRACTION: 1.0,

  /**
   * DOW modifier applied to patients waiting beyond PHA_CRITICAL_WAIT_HOURS.
   * Conservative: 5% increase in DOW risk per 24h beyond threshold.
   * Source: clinical extrapolation, AJMedP-1 §6.4.3 wait-time guidance.
   */
  DOW_WAIT_PENALTY_PER_24H: 0.05,
};

// =============================================================================
// AIRFIELD DEFAULT STATE
// =============================================================================

/**
 * AIRFIELD_DEFAULT_STATE
 * Template for a new airfield object added to strategicEvac.airfields[].
 * Tab3 uses this when the planner adds/enables an airfield.
 *
 * NOTE: schedule is null until planner configures it in Tab3 Section 3F.
 * Calculator must throw if schedule is null and patients require STRATEVAC.
 *
 * is_active:    false for alternate airfield until toggled ON by planner
 * is_primary:   true only for airfields[0]
 */
export const AIRFIELD_DEFAULT_STATE = {
  id: null,                        // set by Tab3 on creation
  label: '',                       // planner-entered name
  class: 'CLASS_B',                // most common tactical airfield
  type: 'MILITARY',
  is_primary: false,
  is_active: false,
  pha_beds: 50,                    // default PHA holding capacity
  farp_enabled: false,
  distances: {
    r2b_to_airfield_km: null,
    r2e_to_airfield_km: null,
    r3_to_airfield_km: null,
    airfield_to_r4_km: null,
  },
  schedule: null,                  // null = not yet configured
  coordinates: null,               // Tab 6 writes here
  distance_source: 'MANUAL',       // Tab 6 → 'MAP_DERIVED'
};

/**
 * PRIMARY_AIRFIELD_DEFAULTS
 * Overrides applied to airfields[0] (primary).
 */
export const PRIMARY_AIRFIELD_DEFAULTS = {
  is_primary: true,
  is_active: true,
  pha_beds: 50,
};

/**
 * ALTERNATE_AIRFIELD_DEFAULTS
 * Overrides applied to airfields[1] (alternate).
 * is_active = false until planner toggles "Enable Alternate Airfield" in Tab3.
 */
export const ALTERNATE_AIRFIELD_DEFAULTS = {
  is_primary: false,
  is_active: false,
  pha_beds: 30,   // alternate typically has smaller PHA
};

// =============================================================================
// UI CONSTANTS
// =============================================================================

/**
 * DEPARTURE_HOUR_OPTIONS
 * Valid departure hours for flight scheduling UI dropdown.
 * Restricted to militarily realistic windows (no 0200 STRATEVAC flights).
 */
export const DEPARTURE_HOUR_OPTIONS = [
  { value: 4,  label: '04:00' },
  { value: 5,  label: '05:00' },
  { value: 6,  label: '06:00' },
  { value: 7,  label: '07:00' },
  { value: 8,  label: '08:00' },
  { value: 9,  label: '09:00' },
  { value: 10, label: '10:00' },
  { value: 11, label: '11:00' },
  { value: 12, label: '12:00' },
  { value: 13, label: '13:00' },
  { value: 14, label: '14:00' },
  { value: 15, label: '15:00' },
  { value: 16, label: '16:00' },
  { value: 17, label: '17:00' },
  { value: 18, label: '18:00' },
  { value: 19, label: '19:00' },
  { value: 20, label: '20:00' },
];

/**
 * INTERVAL_HOUR_OPTIONS
 * Valid repeat intervals for INTERVAL_HOURS frequency mode.
 */
export const INTERVAL_HOUR_OPTIONS = [
  { value: 24,  label: 'Daily (24h)' },
  { value: 36,  label: 'Every 36 hours' },
  { value: 48,  label: 'Every 48 hours (alternate day)' },
  { value: 72,  label: 'Every 72 hours (every 3 days)' },
  { value: 96,  label: 'Every 96 hours (every 4 days)' },
];

/**
 * FLIGHTS_PER_DAY_OPTIONS
 * ENH-STRAT-001: Number of aircraft sorties on each departure day.
 * A single C-130J with 2h turnaround can fly ~6 sorties in a 12h day window.
 * Practical operational limit: 4 sorties per airfield per day (planning conservative).
 * Used by Tab3 schedule UI. Calculator multiplies litter_capacity × flights_per_day.
 */
export const FLIGHTS_PER_DAY_OPTIONS = [
  { value: 1, label: '1 sortie / departure day' },
  { value: 2, label: '2 sorties / departure day' },
  { value: 3, label: '3 sorties / departure day' },
  { value: 4, label: '4 sorties / departure day (operational max)' },
  { value: 5, label: '5 sorties / departure day' },
  { value: 6, label: '6 sorties / departure day (theoretical max)' },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * getAircraftForAirfieldClass
 * Returns aircraft types compatible with a given airfield class.
 * Used to filter aircraft dropdown in Tab3.
 *
 * @param {string} airfieldClass - key from AIRFIELD_CLASSES
 * @returns {string[]} array of aircraft type keys
 */
export function getAircraftForAirfieldClass(airfieldClass) {
  const cls = AIRFIELD_CLASSES[airfieldClass];
  if (!cls) {
    throw new Error(
      `stratevacScheduling.getAircraftForAirfieldClass: unknown airfield class "${airfieldClass}". ` +
      `Valid classes: ${Object.keys(AIRFIELD_CLASSES).join(', ')}`
    );
  }
  return cls.compatible_aircraft;
}

/**
 * getDefaultScheduleForNation
 * Returns the default schedule template for a given national standard.
 * Throws if nation has no template — forces explicit configuration.
 *
 * @param {string} nationalStandard - key from nationalStandards.js
 * @returns {object} schedule template (copy, not reference)
 */
export function getDefaultScheduleForNation(nationalStandard) {
  const template = DEFAULT_SCHEDULE_TEMPLATES[nationalStandard];
  if (!template) {
    throw new Error(
      `stratevacScheduling.getDefaultScheduleForNation: no schedule template for nation ` +
      `"${nationalStandard}". Add entry to DEFAULT_SCHEDULE_TEMPLATES. ` +
      `Available: ${Object.keys(DEFAULT_SCHEDULE_TEMPLATES).join(', ')}`
    );
  }
  return { ...template };
}

/**
 * computeDepartureDays
 * Resolves a schedule config to an array of actual departure day numbers
 * for a given operation duration.
 *
 * @param {object} schedule  - airfield.schedule object
 * @param {number} opDays    - total operation duration in days
 * @returns {number[]}       - sorted array of departure day numbers (1-based)
 */
export function computeDepartureDays(schedule, opDays) {
  if (!schedule) {
    throw new Error('stratevacScheduling.computeDepartureDays: schedule is null.');
  }
  if (!opDays || opDays < 1) {
    throw new Error('stratevacScheduling.computeDepartureDays: opDays must be >= 1.');
  }

  const { frequency_mode, fixed_days, interval_hours, first_departure_day } = schedule;

  if (frequency_mode === 'FIXED_DAYS') {
    if (!fixed_days || fixed_days.length === 0) {
      throw new Error(
        'stratevacScheduling.computeDepartureDays: FIXED_DAYS mode requires fixed_days array.'
      );
    }
    return fixed_days.filter(d => d >= 1 && d <= opDays).sort((a, b) => a - b);
  }

  if (frequency_mode === 'DAILY') {
    const days = [];
    for (let d = (first_departure_day ?? 1); d <= opDays; d++) {
      days.push(d);
    }
    return days;
  }

  if (frequency_mode === 'INTERVAL_HOURS') {
    if (!interval_hours || interval_hours < 1) {
      throw new Error(
        'stratevacScheduling.computeDepartureDays: INTERVAL_HOURS mode requires interval_hours >= 1.'
      );
    }
    const start = first_departure_day ?? 1;
    const intervalDays = interval_hours / 24;
    const days = [];
    let current = start;
    while (current <= opDays) {
      days.push(Math.round(current));
      current += intervalDays;
    }
    return days;
  }

  if (frequency_mode === 'ON_DEMAND') {
    throw new Error(
      'stratevacScheduling.computeDepartureDays: ON_DEMAND is not valid for Tabs 1–5 planning. ' +
      'Tab 6 only.'
    );
  }

  throw new Error(
    `stratevacScheduling.computeDepartureDays: unknown frequency_mode "${frequency_mode}". ` +
    `Valid modes: ${Object.keys(SCHEDULE_FREQUENCY_MODES).join(', ')}`
  );
}

/**
 * getPHAStatus
 * Returns PHA threshold status object for a given occupancy fraction.
 *
 * @param {number} fraction - current_patients / pha_beds (can exceed 1.0)
 * @returns {object}        - matching entry from PHA_THRESHOLDS
 */
export function getPHAStatus(fraction) {
  if (typeof fraction !== 'number' || isNaN(fraction)) {
    throw new Error(
      `stratevacScheduling.getPHAStatus: fraction must be a number, got "${fraction}".`
    );
  }
  const key = Object.keys(PHA_THRESHOLDS).find(k => {
    const t = PHA_THRESHOLDS[k];
    return fraction >= t.min_fraction && fraction < t.max_fraction;
  });
  // MASCAL has max_fraction = Infinity so will always catch overflow
  return PHA_THRESHOLDS[key ?? 'MASCAL'];
}
